import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import { default as api } from '../../utils/api';
import SignatureCanvas from 'react-signature-canvas';

const PrescriptionForm = () => {
    const { consultationId } = useParams();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotification();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Prescription State
    const [prescription, setPrescription] = useState({
        vitals: {
            bloodPressure: '',
            pulse: '',
            temperature: '',
            weight: '',
            spo2: ''
        },
        chiefComplaint: '',
        diagnosis: '',
        medicines: [], // { name, dosage, timing, duration, instructions, beforeAfterFood }
        advice: '',
        generalInstructions: '',
        followUpDate: '',
        followUpInstructions: ''
    });

    useEffect(() => {
        fetchPrescription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consultationId]);

    const fetchPrescription = async () => {
        try {
            setLoading(true);
            // Try to fetch existing draft
            const response = await api.get(`/prescription/consultation/${consultationId}`);

            if (response.data.success && response.data.data) {
                const data = response.data.data;
                setPrescription({
                    chiefComplaint: data.chiefComplaint || '',
                    diagnosis: data.diagnosis || '',
                    vitals: data.vitals || {
                        bloodPressure: '',
                        pulse: '',
                        temperature: '',
                        weight: '',
                        spo2: ''
                    },
                    medicines: data.medicines || [],
                    advice: data.advice || '',
                    generalInstructions: data.generalInstructions || '',
                    followUpDate: data.follow_up_date ? data.follow_up_date.split('T')[0] : '',
                    followUpInstructions: data.followUpInstructions || ''
                });

                if (data.patientDetails) {
                    // details state removed
                }
            }
        } catch (error) {
            // If 404, it means no draft exists. Fetch consultation details to pre-fill.
            if (error.response && error.response.status === 404) {
                try {
                    const consultRes = await api.get(`/consultation/${consultationId}`);
                    if (consultRes.data.success) {
                        const c = consultRes.data.data;
                        // details state removed
                        // Also pre-fill diagnosis if available
                        if (c.diagnosis) {
                            setPrescription(prev => ({ ...prev, diagnosis: c.diagnosis }));
                        }
                    }
                } catch (fetchErr) {
                    console.error("Error fetching consultation details:", fetchErr);
                }
            } else {
                console.error('Error fetching prescription:', error);
                showError('Failed to load prescription details');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPrescription(prev => ({ ...prev, [name]: value }));
    };

    const handleVitalChange = (e) => {
        const { name, value } = e.target;
        setPrescription(prev => ({
            ...prev,
            vitals: {
                ...prev.vitals,
                [name]: value
            }
        }));
    };

    // Medicine Management
    const addMedicine = () => {
        setPrescription(prev => ({
            ...prev,
            medicines: [...prev.medicines, {
                name: '',
                dosage: '',
                timing: { morning: false, afternoon: false, night: false },
                beforeAfterFood: 'after',
                duration: '',
                instructions: ''
            }]
        }));
    };

    const removeMedicine = (index) => {
        setPrescription(prev => ({
            ...prev,
            medicines: prev.medicines.filter((_, i) => i !== index)
        }));
    };

    const handleMedicineChange = (index, field, value) => {
        const newMedicines = [...prescription.medicines];
        if (field === 'timing') {
            newMedicines[index].timing = { ...newMedicines[index].timing, ...value };
        } else {
            newMedicines[index][field] = value;
        }
        setPrescription(prev => ({ ...prev, medicines: newMedicines }));
    };

    const saveDraft = async () => {
        if (saving) return;
        try {
            setSaving(true);
            const payload = {
                consultationId,
                ...prescription,
                followUpDate: prescription.followUpDate || null // Send null if empty string
            };

            const response = await api.post('/prescription/create', payload);

            if (response.data.success) {
                showSuccess('Prescription draft saved');
            } else {
                showError(response.data.message || 'Failed to save draft');
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            showError('Error saving prescription draft');
        } finally {
            setSaving(false);
        }
    };

    // Signature State
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const sigCanvas = useRef(null);

    const clearSignature = () => {
        sigCanvas.current.clear();
    };

    const handleSignatureComplete = async () => {
        // Defensive check for canvas ref
        if (!sigCanvas.current) {
            showError('Signature pad not ready. Please try again.');
            return;
        }

        // Check if empty (using internal method if available, or try/catch)
        try {
            if (sigCanvas.current.isEmpty()) {
                showError('Please provide a signature');
                return;
            }

            // Attempt to get trimmed canvas, fallback to full canvas if library error occurs
            let signatureDataUrl;
            try {
                const signatureCanvas = sigCanvas.current.getTrimmedCanvas();
                signatureDataUrl = signatureCanvas.toDataURL('image/png');
            } catch (innerError) {
                console.warn("Trimmed canvas failed, falling back to full canvas", innerError);
                // Fallback: Access underlying canvas directly
                if (sigCanvas.current.getCanvas) {
                    signatureDataUrl = sigCanvas.current.getCanvas().toDataURL('image/png');
                } else {
                    throw new Error("Cannot access signature canvas");
                }
            }

            if (signatureDataUrl) {
                await completePrescription(signatureDataUrl);
                setShowSignatureModal(false);
            } else {
                showError('Could not capture signature. Please try again.');
            }
        } catch (error) {
            console.error("Signature capture error:", error);
            showError('Error capturing signature. Please try again.');
        }
    };

    const completePrescription = async (digitalSignature = null) => {
        // Validation
        if (!prescription.chiefComplaint || prescription.medicines.length === 0) {
            showError('Please fill Chief Complaint and at least one medicine');
            return;
        }

        // If no signature passed, open modal
        if (!digitalSignature) {
            setShowSignatureModal(true);
            return;
        }

        if (saving) return;
        try {
            setSaving(true);
            // First save/update draft
            await api.post('/prescription/create', {
                consultationId,
                ...prescription,
                followUpDate: prescription.followUpDate || null
            });

            // Then complete
            const draftRes = await api.get(`/prescription/consultation/${consultationId}`);
            if (!draftRes.data.success || !draftRes.data.data) {
                showError('Failed to retrieve draft for completion');
                return;
            }
            const prescriptionId = draftRes.data.data._id;

            const response = await api.post('/prescription/complete', {
                prescriptionId,
                digitalSignature
            });

            if (response.data.success) {
                showSuccess('Prescription sent! Please mark the consultation as Complete.');

                setTimeout(() => {
                    navigate(-1);
                }, 2000);
            } else {
                showError(response.data.message || 'Failed to complete prescription');
            }

        } catch (error) {
            console.error('Error completing prescription:', error);
            showError(error.response?.data?.message || 'Error completing prescription');
        } finally {
            setSaving(false);
        }
    };

    if (loading && !showSignatureModal) return <div className="text-center py-8 text-gold">Loading...</div>;

    return (
        <div className="min-h-screen bg-dark text-light p-6 relative">
            <div className={`max-w-4xl mx-auto space-y-8 ${showSignatureModal ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-gold">Prescription</h1>
                    <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">Cancel</button>
                </div>

                {/* Patient Details (Read-only view) */}
                {/* ... (omitted for brevity, can add later if needed) ... */}

                {/* Vitals Section */}
                <div className="bg-gray-800 p-6 rounded-xl space-y-6">
                    <h2 className="text-lg font-semibold text-gold border-b border-gray-700 pb-2">Vitals</h2>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">BP (mmHg)</label>
                            <input
                                name="bloodPressure"
                                value={prescription.vitals?.bloodPressure || ''}
                                onChange={handleVitalChange}
                                className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-light text-sm focus:border-gold outline-none"
                                placeholder="120/80"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">Pulse (bpm)</label>
                            <input
                                name="pulse"
                                value={prescription.vitals?.pulse || ''}
                                onChange={handleVitalChange}
                                className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-light text-sm focus:border-gold outline-none"
                                placeholder="72"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">Temp (°F)</label>
                            <input
                                name="temperature"
                                value={prescription.vitals?.temperature || ''}
                                onChange={handleVitalChange}
                                className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-light text-sm focus:border-gold outline-none"
                                placeholder="98.6"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">Weight (kg)</label>
                            <input
                                name="weight"
                                value={prescription.vitals?.weight || ''}
                                onChange={handleVitalChange}
                                className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-light text-sm focus:border-gold outline-none"
                                placeholder="65"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-400 mb-1">SpO2 (%)</label>
                            <input
                                name="spo2"
                                value={prescription.vitals?.spo2 || ''}
                                onChange={handleVitalChange}
                                className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-light text-sm focus:border-gold outline-none"
                                placeholder="98"
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800 p-6 rounded-xl space-y-6">
                    {/* Chief Complaint */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Chief Complaint</label>
                        <textarea
                            name="chiefComplaint"
                            value={prescription.chiefComplaint}
                            onChange={handleInputChange}
                            className="w-full bg-gray-900 border border-gray-600 rounded-lg p-3 text-light focus:border-gold outline-none"
                            placeholder="Patient's main symptoms..."
                            rows="3"
                        />
                    </div>

                    {/* Diagnosis */}
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Diagnosis</label>
                        <textarea
                            name="diagnosis"
                            value={prescription.diagnosis}
                            onChange={handleInputChange}
                            className="w-full bg-gray-900 border border-gray-600 rounded-lg p-3 text-light focus:border-gold outline-none"
                            placeholder="Clinical diagnosis..."
                            rows="2"
                        />
                    </div>
                </div>

                {/* Medicines */}
                <div className="bg-gray-800 p-6 rounded-xl space-y-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-semibold text-gold">Medicines</h2>
                        <button onClick={addMedicine} className="text-sm bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded text-white transition-colors">
                            + Add Medicine
                        </button>
                    </div>

                    {prescription.medicines.map((med, index) => (
                        <div key={index} className="bg-gray-900 p-4 rounded-lg relative">
                            <button onClick={() => removeMedicine(index)} className="absolute top-2 right-2 text-gray-500 hover:text-red-500">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <input
                                    placeholder="Medicine Name"
                                    value={med.name}
                                    onChange={(e) => handleMedicineChange(index, 'name', e.target.value)}
                                    className="bg-gray-800 border border-gray-600 rounded p-2 text-light"
                                />
                                <input
                                    placeholder="Dosage (e.g. 500mg)"
                                    value={med.dosage}
                                    onChange={(e) => handleMedicineChange(index, 'dosage', e.target.value)}
                                    className="bg-gray-800 border border-gray-600 rounded p-2 text-light"
                                />
                            </div>

                            <div className="flex flex-wrap gap-4 mb-2">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input type="checkbox" checked={med.timing.morning} onChange={(e) => handleMedicineChange(index, 'timing', { ...med.timing, morning: e.target.checked })} className="rounded bg-gray-800 text-gold focus:ring-gold" />
                                    <span className="text-sm text-gray-400">Morning</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input type="checkbox" checked={med.timing.afternoon} onChange={(e) => handleMedicineChange(index, 'timing', { ...med.timing, afternoon: e.target.checked })} className="rounded bg-gray-800 text-gold focus:ring-gold" />
                                    <span className="text-sm text-gray-400">Afternoon</span>
                                </label>
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input type="checkbox" checked={med.timing.night} onChange={(e) => handleMedicineChange(index, 'timing', { ...med.timing, night: e.target.checked })} className="rounded bg-gray-800 text-gold focus:ring-gold" />
                                    <span className="text-sm text-gray-400">Night</span>
                                </label>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <select value={med.beforeAfterFood} onChange={(e) => handleMedicineChange(index, 'beforeAfterFood', e.target.value)} className="bg-gray-800 border border-gray-600 rounded p-2 text-light text-sm">
                                    <option value="after">After Food</option>
                                    <option value="before">Before Food</option>
                                    <option value="with">With Food</option>
                                    <option value="anytime">Anytime</option>
                                </select>
                                <input
                                    placeholder="Duration (e.g. 5 days)"
                                    value={med.duration}
                                    onChange={(e) => handleMedicineChange(index, 'duration', e.target.value)}
                                    className="bg-gray-800 border border-gray-600 rounded p-2 text-light text-sm"
                                />
                                <input
                                    placeholder="Instructions"
                                    value={med.instructions}
                                    onChange={(e) => handleMedicineChange(index, 'instructions', e.target.value)}
                                    className="bg-gray-800 border border-gray-600 rounded p-2 text-light text-sm"
                                />
                            </div>
                        </div>
                    ))}
                    {prescription.medicines.length === 0 && <p className="text-gray-500 text-center text-sm py-4">No medicines added.</p>}
                </div>

                {/* Advice & Follow-up */}
                <div className="bg-gray-800 p-6 rounded-xl space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Advice / Instructions</label>
                        <textarea
                            name="advice"
                            value={prescription.advice}
                            onChange={handleInputChange}
                            className="w-full bg-gray-900 border border-gray-600 rounded-lg p-3 text-light focus:border-gold outline-none"
                            rows="2"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Follow-up Date</label>
                            <input
                                type="date"
                                name="followUpDate"
                                value={prescription.followUpDate}
                                onChange={handleInputChange}
                                className="w-full bg-gray-900 border border-gray-600 rounded-lg p-3 text-light focus:border-gold"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Follow-up Instructions</label>
                            <input
                                type="text"
                                name="followUpInstructions"
                                value={prescription.followUpInstructions}
                                onChange={handleInputChange}
                                className="w-full bg-gray-900 border border-gray-600 rounded-lg p-3 text-light focus:border-gold"
                                placeholder="e.g. Visit if fever persists"
                            />
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-4 pt-4 pb-10">
                    <button
                        onClick={saveDraft}
                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-colors"
                    >
                        Save Draft
                    </button>
                    <button
                        onClick={() => completePrescription()}
                        className="flex-1 bg-gold hover:bg-yellow-600 text-dark font-bold py-3 px-6 rounded-xl transition-colors shadow-lg shadow-gold/20"
                    >
                        Digitally Sign & Send
                    </button>
                </div>
            </div>

            {/* Signature Modal */}
            {showSignatureModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
                    <div className="bg-gray-800 rounded-xl max-w-lg w-full p-6 shadow-2xl">
                        <h3 className="text-xl font-bold text-gold mb-4 text-center">Doctor's Signature</h3>
                        <p className="text-gray-400 text-sm text-center mb-4">Please sign below to authorize this prescription.</p>

                        <div className="bg-white rounded-lg mb-6 overflow-hidden border-2 border-dashed border-gray-500 hover:border-gold transition-colors">
                            <SignatureCanvas
                                ref={(ref) => { sigCanvas.current = ref; }}
                                penColor="black"
                                canvasProps={{
                                    width: 500,
                                    height: 200,
                                    className: 'signature-canvas w-full h-48 cursor-crosshair'
                                }}
                                backgroundColor="white"
                            />
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowSignatureModal(false)}
                                className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={clearSignature}
                                className="flex-1 py-2 px-4 bg-red-900/50 hover:bg-red-900 text-red-200 border border-red-800 rounded-lg transition-colors"
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleSignatureComplete}
                                className="flex-1 py-2 px-4 bg-gold hover:bg-yellow-600 text-dark font-bold rounded-lg shadow-lg shadow-gold/20 transition-colors"
                            >
                                Confirm & Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrescriptionForm;
